'use client';

import { useEffect } from 'react';
import Image from 'next/image';
import { useSearchParams } from 'next/navigation';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardFooter, CardHeader, CardTitle } from '@/components/ui/card';
import { LogIn } from 'lucide-react';

const ERROR_MESSAGES: Record<string, string> = {
  auth_failed: 'Authentication failed. Please try again.',
  missing_params: 'Invalid response from identity provider.',
  invalid_state: 'Session expired. Please try again.',
  missing_verifier: 'Session expired. Please try again.',
  no_email: 'No email address found in your account.',
  user_not_found: 'User account not found.',
  no_roles: 'Your account has no assigned roles. Contact your administrator.',
  no_workspace: 'No organization assigned to your account. Contact your administrator.',
  callback_failed: 'Authentication failed. Please try again.',
};

export default function LoginPage() {
  const searchParams = useSearchParams();
  const error = searchParams.get('error');
  const redirect = searchParams.get('redirect');

  useEffect(() => {
    if (!error) {
      const url = redirect
        ? `/api/auth/keycloak?redirect=${encodeURIComponent(redirect)}`
        : '/api/auth/keycloak';
      window.location.replace(url);
    }
  }, [error, redirect]);

  const errorMessage = error ? ERROR_MESSAGES[error] || 'An error occurred. Please try again.' : null;

  if (!error) return null;

  const signInUrl = redirect
    ? `/api/auth/keycloak?redirect=${encodeURIComponent(redirect)}`
    : '/api/auth/keycloak';

  return (
    <div className="animate-fade-up">
      <div className="flex flex-col items-center mb-8">
        <Image
          src="/images/Baya_low_Res.png"
          alt="Baya"
          width={280}
          height={80}
          className="h-14 w-auto max-w-[min(100%,280px)] object-contain mb-2"
          priority
        />
        <p className="text-sm text-muted-foreground">by Het AI Bedrijf</p>
      </div>

      <Card className="border-0 shadow-xl">
        <CardHeader className="space-y-1 pb-4">
          <CardTitle className="text-xl">Sign in failed</CardTitle>
        </CardHeader>
        <CardContent className="space-y-4">
          <div className="p-3 text-sm text-red-600 bg-red-50 rounded-lg">{errorMessage}</div>
          <Button asChild className="w-full" size="lg">
            <a href={signInUrl}>
              <LogIn className="mr-2 h-4 w-4" />
              Try again
            </a>
          </Button>
        </CardContent>
        <CardFooter className="flex flex-col gap-4">
          <p className="text-sm text-muted-foreground text-center">
            Need help? Contact your administrator.
          </p>
        </CardFooter>
      </Card>
    </div>
  );
}
